import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2, Truck, X } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { SupplierDialog } from '@/components/suppliers/SupplierDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useDeleteSupplier, useSupplierPage } from '@/hooks/useSuppliers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAuth } from '@/contexts/AuthContext';
import { supplierDisplayName } from '@/lib/supplierOptions';
import type { Supplier } from '@/services/supplierService';

const PAGE_SIZE = 25;

function RowAction({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export default function Suppliers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [dialog, setDialog] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null });
  const [pendingDelete, setPendingDelete] = useState<Supplier | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const deleteSupplier = useDeleteSupplier();
  const { data, isLoading, isFetching, error } = useSupplierPage(debouncedSearch, pageNumber, PAGE_SIZE);

  // A new search always starts from the first page
  useEffect(() => setPageNumber(1), [debouncedSearch]);

  const rows = data?.items ?? [];
  const hasNext = data?.hasNextPage ?? false;
  const firstShown = rows.length ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = (pageNumber - 1) * PAGE_SIZE + rows.length;
  const columnCount = isAdmin ? 6 : 5;
  const hasContent = !error && !isLoading && rows.length > 0;

  const openCreate = () => setDialog({ open: true, supplier: null });
  const openEdit = (supplier: Supplier) => setDialog({ open: true, supplier });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteSupplier.mutate(pendingDelete.id, {
      onSuccess: () => {
        // Deleting the last row of a page would leave an empty page behind
        if (rows.length === 1 && pageNumber > 1) setPageNumber((p) => p - 1);
      },
      onSettled: () => setPendingDelete(null),
    });
  };

  return (
    <Layout>
      <ScrollPage
        framed={hasContent}
        footer={
          hasContent ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Showing {firstShown}–{lastShown}
                {isFetching && ' …'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber <= 1 || isFetching}
                  onClick={() => setPageNumber((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {pageNumber}</span>
                <Button variant="outline" size="sm" disabled={!hasNext || isFetching} onClick={() => setPageNumber((p) => p + 1)}>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null
        }
        header={
          <>
            <PageHeader
              title="Suppliers"
              description="Suppliers available when creating contracts"
              actions={
                isAdmin && (
                  <Button onClick={openCreate}>
                    <Plus className="h-4 w-4" />
                    Add supplier
                  </Button>
                )
              }
            />

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative w-full sm:w-96">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search suppliers"
                  placeholder="Search by name, code or business unit"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {search && (
                <Button variant="ghost" onClick={() => setSearch('')} className="text-muted-foreground">
                  <X className="mr-1.5 h-4 w-4" />
                  Clear search
                </Button>
              )}
            </div>
          </>
        }
      >
        {error ? (
          <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            Suppliers could not be loaded. Please try again later.
          </div>
        ) : isLoading ? (
          <Card className="overflow-hidden" aria-busy="true" aria-label="Loading suppliers">
            <div className="divide-y">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="flex items-center gap-6 px-6 py-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="hidden h-4 w-32 md:block" />
                  <Skeleton className="ml-auto h-4 w-20" />
                </div>
              ))}
            </div>
          </Card>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed px-6 py-16 text-center">
            <Truck className="h-9 w-9 text-muted-foreground/60" aria-hidden="true" />
            <p className="mt-4 text-base font-semibold">{debouncedSearch ? 'No suppliers match your search' : 'No suppliers yet'}</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {debouncedSearch
                ? 'Try a different name, code or business unit.'
                : 'Add the first supplier so it can be selected on contracts.'}
            </p>
            {!debouncedSearch && isAdmin && (
              <Button className="mt-6" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Add supplier
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="min-w-[14rem] pl-6">Supplier</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="hidden md:table-cell">Business unit</TableHead>
                <TableHead className="hidden xl:table-cell">Business unit key</TableHead>
                <TableHead className="hidden lg:table-cell">Contact email</TableHead>
                {isAdmin && (
                  <TableHead className="w-[100px] pr-6 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell className="pl-6">
                    <span className="font-medium text-foreground">{supplierDisplayName(supplier)}</span>
                    {supplier.name && supplier.name !== supplierDisplayName(supplier) && (
                      <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{supplier.name}</p>
                    )}
                  </TableCell>
                  <TableCell>{supplier.code}</TableCell>
                  <TableCell className="hidden md:table-cell">{supplier.businessUnitName}</TableCell>
                  <TableCell className="hidden xl:table-cell">{supplier.businessUnitKey}</TableCell>
                  <TableCell className="hidden lg:table-cell">{supplier.contactEmail}</TableCell>
                  {isAdmin && (
                    <TableCell className="pr-6">
                      <div className="flex items-center justify-end gap-1">
                        <RowAction label="Edit">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Edit ${supplierDisplayName(supplier)}`}
                            onClick={() => openEdit(supplier)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </RowAction>
                        <RowAction label="Delete">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${supplierDisplayName(supplier)}`}
                            onClick={() => setPendingDelete(supplier)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </RowAction>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ScrollPage>

      <SupplierDialog
        open={dialog.open}
        supplier={dialog.supplier}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
      />

      <ConfirmActionDialog
        open={!!pendingDelete}
        destructive
        isPending={deleteSupplier.isPending}
        title="Delete supplier?"
        description={`"${pendingDelete ? supplierDisplayName(pendingDelete) : ''}" will be removed from the supplier list and can no longer be selected on new contracts.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Layout>
  );
}
